import { effectScope, watchEffect, type Directive, type EffectScope } from 'vue'

import { useAuthStore } from '@/stores/auth'
import type { PermissionCode } from '@/types'

type PermissionInput = PermissionCode | readonly PermissionCode[]

interface PermissionDirectiveState {
  scope: EffectScope
  value: PermissionInput
  originalDisplay: string
}

const states = new WeakMap<HTMLElement, PermissionDirectiveState>()

function normalizePermissionInput(value: PermissionInput): readonly PermissionCode[] {
  return Array.isArray(value) ? value : [value]
}

function canShow(value: PermissionInput): boolean {
  const permissions = normalizePermissionInput(value)
  if (permissions.length === 0) return true

  const authStore = useAuthStore()
  return authStore.canAny([...permissions])
}

function updateElementVisibility(el: HTMLElement, value: PermissionInput, originalDisplay: string): void {
  if (canShow(value)) {
    el.style.display = originalDisplay
    el.removeAttribute('aria-hidden')
  } else {
    el.style.display = 'none'
    el.setAttribute('aria-hidden', 'true')
  }
}

const permissionDirective: Directive<HTMLElement, PermissionInput> = {
  mounted(el, binding) {
    const state: PermissionDirectiveState = {
      scope: effectScope(),
      value: binding.value,
      originalDisplay: el.style.display,
    }
    states.set(el, state)

    state.scope.run(() => {
      watchEffect(() => {
        updateElementVisibility(el, state.value, state.originalDisplay)
      })
    })
  },
  updated(el, binding) {
    const state = states.get(el)
    if (!state) return
    state.value = binding.value
    updateElementVisibility(el, state.value, state.originalDisplay)
  },
  unmounted(el) {
    const state = states.get(el)
    if (state) {
      state.scope.stop()
      states.delete(el)
    }
  },
}

export default permissionDirective
